import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Drawer, Switch,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllTenants,
  getUser,
  handleTenantSwitch,
  getCookie,
} from '../../../utils/utility';
import { ResponsiveHeading } from '../../../styles/heading';
import { StyledTextField } from '../../../styles/textField';
import { cyanBlueBackgroundButtonStyles } from '../../../styles/button';
import { ResponsiveTypography } from '../../../styles/inputLabel';
import { AppDispatch, RootState } from '../../../Redux/store';
import {
  updateCurrentTenantName,
  updateTenantChangedStatus,
} from '../../../Redux/Slices/Customer/customer';
import DeleteModal from '../../../components/modal/deleteModal';
import MainLayout from '../../../components/MainLayout';

const StyledTableCell = styled(TableCell)(({ theme }: any) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }: any) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface AddTenantModalProps {
  open: boolean;
  setOpen: Function;
  tenants: any;
  setReloadTenants: Function;
}

export function AddTenantModal({
  open,
  setOpen,
  tenants,
  setReloadTenants,
}: AddTenantModalProps) {
  const [tenantName, setTenantName] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const handleClose = () => {
    setOpen(!open);
  };
  const handleAddTenantForm = (event: any) => {
    setTenantName(event.target.value);
  };
  const handleAddTenant = async () => {
    const token = await getCookie('token');
    let tenantNameUnique: boolean = true;
    tenants?.forEach((tenant: any) => {
      if (tenant.tenant_name === tenantName) {
        tenantNameUnique = false;
      }
    });
    if (!tenantNameUnique) {
      toast.error('Tenant Name Already Exists!');
    } else {
      axios
        .post(
          `${process.env.REACT_APP_NODE_BACKEND_URL}/api/add-tenant`,
          {
            tenant_name: tenantName,
          },
          {
            headers: {
              Authorization: token,
            },
          },
        )
        .then(async () => {
          setOpen(!open);
          dispatch(updateCurrentTenantName(tenantName));
          setReloadTenants((prev: any) => !prev);
          setTenantName('');
          await handleTenantSwitch({ tenantName });
        })
        .catch(() => {
          toast.error(process.env.REACT_APP_ADD_TENANT_ERROR);
        });
    }
  };
  return (
    <Drawer open={open} onClose={handleClose} anchor="right">
      <Box px="2.5rem" py="1.5rem" width="25vw">
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          borderBottom="0.063rem solid #EBEBEB"
        >
          <ResponsiveHeading>Configure Tenant</ResponsiveHeading>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{ overflowY: 'hidden', gap: '2rem' }}
          pt="2rem"
        >
          <Box display="flex" flexDirection="column" gap="0.5rem">
            <ResponsiveTypography>Tenant Identifier</ResponsiveTypography>
            <StyledTextField
              id="tenant-name"
              value={tenantName}
              onChange={handleAddTenantForm}
            />
          </Box>
          <Button
            variant="text"
            sx={cyanBlueBackgroundButtonStyles}
            onClick={handleAddTenant}
          >
            Add Tenant
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

function AddTenantView() {
  const [tenants, setTenants] = useState<any>();
  const [reloadTenants, setReloadTenants] = useState(false);
  const [openAddTenantModal, setOpenAddTenantModal] = useState(false);
  const [activeTenant, setActiveTenant] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(6);
  const [openDialog, setOpenDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string>('');
  const dispatch = useDispatch<AppDispatch>();
  const handleOpenModal = (name: string) => {
    setRowToDelete(name);
    setOpenDialog(true);
  };
  const { currentActiveTenantName, tenantChanged } = useSelector(
    (state: RootState) => state.customer,
  );
  const totalPages = Math.ceil((tenants?.length || 0) / rowsPerPage);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const identifierValue = urlParams.has('open') ? urlParams.get('open') : '';
  useEffect(() => {
    if (identifierValue === 'true') {
      setOpenAddTenantModal(true);
    }
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    async function fetchAllTenants() {
      try {
        const response = await getAllTenants();
        setTenants(response);
      } catch (error) {
        console.error('Error fetching tenants', error);
      }
    }
    fetchAllTenants();
  }, [reloadTenants]);

  const handleNextPage = () => {
    setPage((prevPage) => Math.min(prevPage + 1, totalPages - 1));
  };

  const handlePrevPage = () => {
    setPage((prevPage) => Math.max(prevPage - 1, 0));
  };

  const paginatedData = tenants?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  useEffect(() => {
    async function getCurrentActiveTenant() {
      const user = await getUser();
      tenants?.forEach((tenant: any) => {
        if (tenant?._id === user?.current_active_tenant_id) {
          setActiveTenant(tenant.tenant_name);
        }
      });
    }
    getCurrentActiveTenant();
  }, [tenants, reloadTenants, tenantChanged]);

  const handleAddTenantModal = () => {
    setOpenAddTenantModal(true);
  };
  useEffect(() => {
    if (activeTenant) {
      dispatch(updateCurrentTenantName(activeTenant));
    }
  }, [activeTenant, dispatch]);

  const userSwitchTenant = async (selectedTenant: string) => {
    setActiveTenant(selectedTenant);

    try {
      await handleTenantSwitch({ tenantName: selectedTenant });
      setReloadTenants(!reloadTenants);
    } catch (error) {
      toast.error('Error switching tenant');
      setActiveTenant(activeTenant);
    }
  };

  const deleteTenant = async (selectedTenant: string) => {
    setReloadTenants(!reloadTenants);
    const token = await getCookie('token');
    const isActiveTenant = activeTenant === selectedTenant;
    if (isActiveTenant) {
      toast.error('Cannot Delete Current Active Tenant');
    } else {
      axios
        .post(
          `${process.env.REACT_APP_NODE_BACKEND_URL}/api/delete-tenant`,
          {
            tenant_name: selectedTenant,
          },
          {
            headers: {
              Authorization: token,
            },
          },
        )
        .then(async () => {
          setTenants((prevTenants: any) => prevTenants.filter(
            (tenant: any) => tenant.tenant_name !== selectedTenant,
          ));
          dispatch(updateTenantChangedStatus(true));
          toast.success('Deleted Tenant');
        });
    }
  };
  const handleConfirmRevoke = () => {
    setReloadTenants(true);
    deleteTenant(rowToDelete);
    setOpenDialog(false);
  };

  useEffect(() => {
    if (tenantChanged) {
      dispatch(updateCurrentTenantName(tenantChanged));
      userSwitchTenant(currentActiveTenantName);
    }
  }, [currentActiveTenantName]);

  return (
    <MainLayout>
      <Box display="flex" overflow="hidden" flexDirection="column">
        <AddTenantModal
          open={openAddTenantModal}
          setOpen={setOpenAddTenantModal}
          tenants={tenants}
          setReloadTenants={setReloadTenants}
        />
        <Box display="flex" px="2rem" py="1rem" justifyContent="space-between">
          <Box>
            <Typography variant="h5">Tenant Settings</Typography>
          </Box>
          <Box>
            <Button
              sx={{
                backgroundColor: '#2D3748',
                color: '#ffffff',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#2D3748',
                  color: '#ffffff',
                },
              }}
              onClick={handleAddTenantModal}
            >
              <AddIcon />
              Add New Tenant
            </Button>
          </Box>
        </Box>
        <Box>
          <TableContainer
            component={Paper}
            sx={{ padding: '2rem', height: '75vh', overflow: 'auto' }}
          >
            <Table sx={{ width: '95%' }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Tenant Name</StyledTableCell>
                  <StyledTableCell align="center">URL</StyledTableCell>
                  <StyledTableCell align="center">Enable</StyledTableCell>
                  <StyledTableCell align="left"> Delete</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData?.map((tenant: any) => (
                  <StyledTableRow>
                    <StyledTableCell align="left">
                      {tenant?.tenant_name}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {tenant?.url}
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Switch
                        checked={tenant.tenant_name === currentActiveTenantName}
                        onClick={(e) => {
                          e.preventDefault();
                          dispatch(updateCurrentTenantName(tenant.tenant_name));
                          userSwitchTenant(tenant.tenant_name);
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      {tenant.tenant_name === 'default'
                      || tenant.tenant_name === currentActiveTenantName ? (
                        <Button sx={{ cursor: 'not-allowed' }}>
                          <DeleteIcon sx={{ color: 'grey' }} />
                        </Button>
                        ) : (
                          <Button
                            onClick={() => handleOpenModal(tenant.tenant_name)}
                          >
                            <DeleteIcon sx={{ color: 'red' }} />
                          </Button>
                        )}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
                {!tenants && (
                  <StyledTableRow>
                    <StyledTableCell align="center" />
                    <StyledTableCell align="center">
                      No Configurations
                    </StyledTableCell>
                    <StyledTableCell align="center" />
                    <StyledTableCell align="center" />
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '1rem',
                marginRight: '5rem',
              }}
            >
              <Button onClick={handlePrevPage} disabled={page === 0}>
                Previous
              </Button>
              <Button
                onClick={handleNextPage}
                disabled={page === totalPages - 1}
              >
                Next
              </Button>
            </div>
          </TableContainer>
          <DeleteModal
            open={openDialog}
            title="Confirm Tenant Deletion"
            message="Are you sure you want to delete the selected Tenant ?"
            onConfirm={handleConfirmRevoke}
            onCancel={handleCloseDialog}
          />
        </Box>
      </Box>
    </MainLayout>
  );
}

export default AddTenantView;
